import {useCallback} from 'react';
import useNavigation from '@/hooks/use-navigation';

interface GoBackOptions {
  /** When `true`, always navigate to `fallbackUrl` instead of going back in history. */
  forceFallbackUrl?: boolean;
  /** When `true`, the fallback navigation replaces the current history entry. */
  replace?: boolean;
}

/**
 * Attempts browser-history back navigation. Returns `true` if the browser had
 * a previous history entry to go back to, `false` otherwise.
 *
 * When `false` is returned and a `fallbackUrl` is provided, the hook
 * automatically navigates to that URL instead.
 *
 * Uses React Router v6's `history.state.idx` to determine whether a previous
 * entry exists — this avoids the need to call `goBack()` and then race against
 * an async URL change check.
 */
function useGoBack() {
  const {navigate, goBack} = useNavigation();

  const tryGoBack = useCallback(
    (
      fallbackUrl?: string,
      {forceFallbackUrl = false, replace = false}: GoBackOptions = {}
    ) => {
      const canGoBack = (window.history.state?.idx ?? 0) > 0;

      if (canGoBack && !forceFallbackUrl) {
        goBack();
        return true;
      }

      if (fallbackUrl) {
        navigate(fallbackUrl, {replace});
      }
      return false;
    },
    [goBack, navigate]
  );

  return tryGoBack;
}

export default useGoBack;
